import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DatasetCard = ({ dataset, onDownload, onPreview }) => {
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024))?.toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024))?.toFixed(2)} GB`;
  };

  const getAccessBadgeColor = (level) => {
    switch (level) {
      case 'Public': return 'bg-[var(--color-success)] text-[var(--color-success-foreground)]';
      case 'Restricted': return 'bg-[var(--color-warning)] text-[var(--color-warning-foreground)]';
      case 'Confidential': return 'bg-[var(--color-error)] text-[var(--color-error-foreground)]';
      default: return 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]';
    }
  };

  return (
    <div className="bg-[var(--color-card)] rounded-lg p-6 border border-[var(--color-border)] hover:shadow-conservation-lg animation-organic">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-[var(--color-muted)]">
          <Image 
            src={dataset?.thumbnail} 
            alt={dataset?.thumbnailAlt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="text-lg font-semibold text-[var(--color-card-foreground)] line-clamp-2">
              {dataset?.title}
            </h3>
            <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${getAccessBadgeColor(dataset?.accessLevel)}`}>
              {dataset?.accessLevel}
            </span>
          </div>
          <p className="text-sm text-[var(--color-muted-foreground)] line-clamp-2 mb-3">
            {dataset?.description}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Calendar" size={16} className="text-[var(--color-muted-foreground)]" />
          <span className="text-sm text-[var(--color-muted-foreground)]">{dataset?.lastUpdated}</span>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="HardDrive" size={16} className="text-[var(--color-muted-foreground)]" />
          <span className="text-sm text-[var(--color-muted-foreground)]">{formatFileSize(dataset?.size)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="FileText" size={16} className="text-[var(--color-muted-foreground)]" />
          <span className="text-sm text-[var(--color-muted-foreground)]">{dataset?.format}</span>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="Download" size={16} className="text-[var(--color-muted-foreground)]" />
          <span className="text-sm text-[var(--color-muted-foreground)]">{dataset?.downloads} downloads</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {dataset?.tags?.map((tag, index) => (
          <span 
            key={index}
            className="px-2 py-1 bg-[var(--color-muted)] text-[var(--color-muted-foreground)] rounded text-xs"
          >
            {tag}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-3 pt-4 border-t border-[var(--color-border)]">
        <Button 
          variant="default" 
          size="sm"
          iconName="Download"
          iconPosition="left"
          onClick={() => onDownload(dataset)}
          className="flex-1"
        >
          Download
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          iconName="Eye"
          iconPosition="left"
          onClick={() => onPreview(dataset)}
          className="flex-1"
        >
          Preview
        </Button>
      </div>
    </div>
  );
};

export default DatasetCard;